import React, { useEffect, useMemo } from 'react';
import { useCallback, useRef, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { type CSSObject } from '@emotion/react';
import { Box, GlobalStyles, Typography } from '@mui/material';
import { captureException } from '@sentry/react';
import { type Editor } from '@tiptap/core';
import { useEditor } from '@tiptap/react';
import {
	LinkBubbleMenu,
	TableBubbleMenu,
	insertImages,
	RichTextField,
	RichTextEditorProvider,
} from 'mui-tiptap';
import { useSnackbar } from 'notistack';

import {
	FileTooLargeError,
	processFile,
} from '@ivy/components/molecules/FileUpload/FileUpload';
import LoadingOverlay from '@ivy/components/molecules/LoadingOverlay';
import { formatInteger } from '@ivy/lib/formatting/number';
import {
	type HeaderLevel,
	headerNumericalLevels,
} from '@ivy/lib/styling/header';

import EditorMenuControls from './EditorMenuControls';
import useExtensions from './useExtensions';

import type { EditorOptions } from '@tiptap/core';

function fileListToImageFiles(fileList: FileList): File[] {
	// You may want to use a package like attr-accept
	// (https://www.npmjs.com/package/attr-accept) to restrict to certain file
	// types.
	return Array.from(fileList).filter((file) => {
		const mimeType = (file.type || '').toLowerCase();
		return mimeType.startsWith('image/');
	});
}

export interface TextEditorProps {
	/** Placeholder text shown when empty content */
	placeholder?: string;
	/** Initial HTML content in the editor */
	content?: string;
	/**
	 * Maximum number of characters allowed in content. If the initial content has more than `maxCharacters`, note
	 * that it is not truncated. This only applies to new edits.
	 */
	maxCharacters?: number;
	/**
	 * Limit the headers that can be used. If specified, the styles of the headers
	 * will start from the specified level, although the underlying HTML tag will still start from `h1`. For example,
	 * specifying `startingHeaderLevel=4` means that only h1, h2, and h3 will be enabled, but they will have the
	 * styles of h4, h5, and h6.
	 */
	startingHeaderLevelStyles?: HeaderLevel;
	useEditorProps?: Partial<EditorOptions>;
}

const TextEditor = React.forwardRef<Editor, TextEditorProps>(
	(
		{
			placeholder,
			content,
			maxCharacters,
			startingHeaderLevelStyles = headerNumericalLevels[
				headerNumericalLevels.length - 1
			],
			useEditorProps,
		},
		ref,
	) => {
		// Maps the header in HTML to the style it should receive. For example, if the `startingHeaderLevel` is 3, then
		// the map will be { 1: 3, 2: 4, 3: 5, 4: 6, 5: 6, 6: 6 }. From here, you can see that h1 will receive
		// h3 styles. Note that h5/h6 will receive the smallest (h6) styles.
		const headerStylesMapping = useMemo(() => {
			const adjustment =
				startingHeaderLevelStyles -
				headerNumericalLevels[headerNumericalLevels.length - 1];
			return headerNumericalLevels.reduce(
				(tot, el) => ({
					...tot,
					// Don't go beyond h6
					[el]: Math.min(el + adjustment, headerNumericalLevels[0]),
				}),
				{},
			);
		}, [startingHeaderLevelStyles]);

		// If a `startingHeaderLevel` has been requested, we'll only enable a subset of headers starting from h1,
		// although their style will start from `startingHeaderLevel`. For example, if the `startingHeaderLevel` is
		// 4, then only headers [1, 2, 3] will be enabled, which will have the styles of h4, h5, and h6 even though
		// they will be rendered as h1, h2, h3.
		const headerLevels = useMemo(() => {
			return headerNumericalLevels
				.map((el) => el - startingHeaderLevelStyles + 1)
				.filter(
					(el) => el >= headerNumericalLevels[headerNumericalLevels.length - 1],
				) as HeaderLevel[];
		}, [startingHeaderLevelStyles]);

		const rteRef = useRef<Editor | null>(null);
		const extensions = useExtensions({
			placeholder,
			maxCharacters,
			headerLevels,
		});
		const [loading, setLoading] = useState(false);
		const client = useApolloClient();
		const { enqueueSnackbar } = useSnackbar();
		const handleUploadFiles = useCallback(
			async (files: File[]) => {
				setLoading(true);
				const compressor = async (file) => {
					const { default: imageCompression } = await import(
						'browser-image-compression'
					);
					return imageCompression(file, {
						maxSizeMB: 3,
					});
				};

				try {
					return await Promise.all(
						files.map(async (file) => {
							const apiFile = await processFile(client, file, {
								compressor: compressor,
								maxFileSize: 3,
							});
							return {
								src: apiFile.publicUrl,
								alt: file.name,
							};
						}),
					);
				} catch (e) {
					if (e instanceof FileTooLargeError) {
						enqueueSnackbar(e.message, { variant: 'error' });
					} else {
						captureException(e);
						console.error(e);
						enqueueSnackbar('An error occurred, please try again.', {
							variant: 'error',
						});
					}
					return [];
				} finally {
					setLoading(false);
				}
			},
			[setLoading, client, enqueueSnackbar],
		);

		const handleNewImageFiles = useCallback(
			async (files: File[], insertPosition?: number): Promise<void> => {
				if (!rteRef.current) {
					return;
				}
				const uploads = await handleUploadFiles(files);
				if (uploads.length) {
					insertImages({
						images: uploads,
						editor: rteRef.current,
						position: insertPosition,
					});
				}
			},
			[rteRef, handleUploadFiles],
		);

		// Allow for dropping images into the editor
		const handleDrop: NonNullable<EditorOptions['editorProps']['handleDrop']> =
			useCallback(
				(view, event, _slice, _moved) => {
					if (!(event instanceof DragEvent) || !event.dataTransfer) {
						return false;
					}

					const imageFiles = fileListToImageFiles(event.dataTransfer.files);
					if (imageFiles.length > 0) {
						const insertPosition = view.posAtCoords({
							left: event.clientX,
							top: event.clientY,
						})?.pos;

						handleNewImageFiles(imageFiles, insertPosition);

						// Return true to treat the event as handled. We call preventDefault
						// ourselves for good measure.
						event.preventDefault();
						return true;
					}

					return false;
				},
				[handleNewImageFiles],
			);

		// Allow for pasting images
		const handlePaste: NonNullable<
			EditorOptions['editorProps']['handlePaste']
		> = useCallback(
			(_view, event, _slice) => {
				if (!event.clipboardData) {
					return false;
				}

				const pastedImageFiles = fileListToImageFiles(
					event.clipboardData.files,
				);
				if (pastedImageFiles.length > 0) {
					handleNewImageFiles(pastedImageFiles);
					// Return true to mark the paste event as handled. This can for
					// instance prevent redundant copies of the same image showing up,
					// like if you right-click and copy an image from within the editor
					// (in which case it will be added to the clipboard both as a file and
					// as HTML, which Tiptap would otherwise separately parse.)
					return true;
				}

				// We return false here to allow the standard paste-handler to run.
				return false;
			},
			[handleNewImageFiles],
		);

		const editor = useEditor({
			extensions: extensions,
			content: content || '',
			editorProps: {
				handleDrop: handleDrop,
				handlePaste: handlePaste,
			},
			...useEditorProps,
		});

		useEffect(() => {
			rteRef.current = editor;
			if (!ref) {
				return;
			}
			if (typeof ref === 'function') {
				ref(editor);
			} else {
				ref.current = editor;
			}
		});

		if (!editor) {
			return null;
		}

		return (
			<>
				<GlobalStyles
					styles={(theme) =>
						Object.entries(headerStylesMapping).reduce(
							(tot, [k, v]) => ({
								...tot,
								[`li[data-value="Heading ${k}"] span`]: {
									...theme.typography[`h${v}`],
								} as CSSObject,
							}),
							{},
						)
					}
				/>
				<Box
					sx={(theme) => ({
						// Position relative for the loading overlay
						position: 'relative',
						// Border radius with overflow hidden to also cut off the overlay
						borderRadius: `${theme.shape.borderRadius}px`,
						overflow: 'hidden',
						// Increase specificity with &&
						// https://stackoverflow.com/questions/67417283/what-does-the-double-ampersand-selector-do-overriding-root-class-using-mak
						// Here, we undo the styling that `mui-tiptap` applies in preference of our theming.
						'&& .ProseMirror': {
							'& h1, & h2, & h3, & h4, & h5, & h6': {
								scrollMarginTop: 50,
							},
							'& h1, & h2, & h3, & h4, & h5, & h6, & p, & ul, & ol': {
								marginBlockStart: 0,
								marginBlockEnd: 'revert',
							},
							// Ensure p has no margin inside tables (like in Tiptap's example CSS https://tiptap.dev/api/nodes/table)
							'& table p': {
								margin: 0,
							},
							// Ensure p has no margin inside task lists (like in Tiptap's example CSS https://tiptap.dev/api/nodes/task-list)
							'& ul[data-type="taskList"] p': {
								margin: 0,
							},
							// Shift header styles according to `startingHeaderLevel`
							...Object.entries(headerStylesMapping).reduce(
								(tot, [k, v]) => ({
									...tot,
									[`& h${k}`]: {
										...theme.typography[`h${v}`],
									},
								}),
								{},
							),
						},
					})}
				>
					<LoadingOverlay
						loading={loading}
						sx={{
							zIndex: 1,
						}}
					/>
					<RichTextEditorProvider editor={editor}>
						<RichTextField
							controls={
								<EditorMenuControls onUploadFiles={handleUploadFiles} />
							}
							variant='outlined'
							footer={
								maxCharacters ? (
									<Box
										sx={{
											borderTopStyle: 'solid',
											borderTopWidth: 1,
											borderTopColor: (theme) => theme.palette.divider,
											py: 1,
											px: 1.5,
										}}
									>
										<Typography variant='body2' color='text.icon'>
											{formatInteger(
												editor.storage.characterCount.characters(),
											)}
											/{formatInteger(maxCharacters)} characters
										</Typography>
									</Box>
								) : undefined
							}
						/>
						<LinkBubbleMenu />
						<TableBubbleMenu />
					</RichTextEditorProvider>
				</Box>
			</>
		);
	},
);

export default TextEditor;
